import { api, ApiError } from './client'

/**
 * C-09 — link sicuro al cliente. Lato backoffice (`/api/contracts/{id}/customer-links`, flag `portale_cliente`) e lato
 * pubblico (`/api/public/customer-link/*`, senza login). Nelle chiamate pubbliche il token e la sessione viaggiano in due
 * header, mai nell'URL delle API.
 */
export type CustomerLinkState = 'Attivo' | 'Scaduto' | 'Revocato' | 'Completato' | 'Bloccato'

export interface CustomerLinkView {
  id: number
  createdAt: string
  createdBy: number | null
  expiresAt: string
  state: CustomerLinkState
  verifiedAt: string | null
  completedAt: string | null
  revokedAt: string | null
  uploadedCount: number
  dataConfirmed: boolean | null
  corrections: string | null
  ibanMasked: string | null
}

export const customerLinksApi = {
  list: (contractId: number) => api<CustomerLinkView[]>(`/api/contracts/${contractId}/customer-links`),
  create: (contractId: number) =>
    api<{ id: number; url: string; expiresAt: string }>(`/api/contracts/${contractId}/customer-links`, { method: 'POST' }),
  revoke: (contractId: number, id: number) =>
    api<void>(`/api/contracts/${contractId}/customer-links/${id}/revoke`, { method: 'POST' }),
  iban: (contractId: number, id: number) =>
    api<{ iban: string }>(`/api/contracts/${contractId}/customer-links/${id}/iban`),
}

// ── Pubblico ────────────────────────────────────────────────────────────────────────────────

export interface PublicSubject {
  practice: string | null
  firstName: string | null
  lastName: string | null
  companyName: string | null
  fiscalCode: string | null
  vatNumber: string | null
  email: string | null
  phone: string | null
  address: string | null
  postalCode: string | null
  city: string | null
  province: string | null
  uploadedCount: number
  kinds: { value: string; label: string }[]
}

const BASE = '/api/public/customer-link'

async function call<T>(token: string, session: string | null, path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set('X-Customer-Link', token)
  if (session) headers.set('X-Customer-Link-Session', session)
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json')
  const res = await fetch(`${BASE}${path}`, { ...init, headers, credentials: 'omit', cache: 'no-store' })
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { message?: string; reason?: string }
    throw new ApiError(body.message ?? 'Errore imprevisto. Riprova.', body.reason, body, res.status)
  }
  return res.json() as Promise<T>
}

export const publicCustomerLinkApi = {
  open: (token: string) => call<{ emailHint: string | null; expiresAt: string }>(token, null, '/open', { method: 'POST' }),
  sendOtp: (token: string) => call<{ sent: boolean }>(token, null, '/otp', { method: 'POST' }),
  verifyOtp: (token: string, code: string) =>
    call<{ session: string }>(token, null, '/otp/verify', { method: 'POST', body: JSON.stringify({ code }) }),
  data: (token: string, session: string) => call<PublicSubject>(token, session, '/data'),
  upload: (token: string, session: string, file: File, kind: string) => {
    const form = new FormData()
    form.append('File', file)
    form.append('Kind', kind)
    return call<{ uploaded: boolean; uploadedCount: number }>(token, session, '/documents', { method: 'POST', body: form })
  },
  submit: (token: string, session: string, dataConfirmed: boolean, corrections: string, iban: string) =>
    call<{ completed: boolean }>(token, session, '/submit', {
      method: 'POST',
      body: JSON.stringify({ dataConfirmed, corrections: corrections || null, iban: iban || null }),
    }),
}
